import type { ISiteContext } from '@wix/app-market-core';
import { PageNotFoundError } from '@wix/app-market-core';
import type { getReviewsSectionResponse } from './app-page-sections/reviews/reviews-api';
import { getReviewsSectionData } from './app-page-sections/reviews/reviews-api';
import type { Plan } from '@wix/app-market-components';
import { AppType } from '@wix/app-market-components';
import {
  SortType,
  type App,
  type AppDataType,
  type Benefits,
  type CurrencySettings,
  type ITraceableTrader,
  type ManagedApp,
  type MarketListingBase,
  type Media,
  type Permission,
  type Services,
  type TaxDetails,
} from '@wix/app-market-services';
import type { SupportedAppStatuses } from '@wix/app-market-services-types';
import { getAppNotification } from './app-page-sections/notifications/api';
import type { AppNotification } from './app-page-sections/notifications/types';
import { getSubCategories } from './app-page-sections/quick-info/api';
import { getAppPricingPlans } from './app-page-sections/pricing-plans';
import type Experiments from '@wix/wix-experiments';
import type { Subcategory } from './app-page-sections/categories-tags';
import { isAppSupportedInResponsiveEditors } from './app-page-sections/notifications/editor-not-supported/api';
import type { AppFetchOptions } from './types';
import { RESELLER_FLOW_APPS } from './reseller-flow-consts';

export interface getAppPageDataResponse {
  app: Omit<AppDataType, 'company'> & {
    type: AppType;
    isUpgradable: boolean;
    instanceId?: string;
    gTag?: string;
    termsOfUseUrl?: string;
  };
  permissions: Permission[] | undefined;
  managedApp?: ManagedApp;
  overview: {
    benefits: Benefits[] | undefined;
    description: string;
    demoUrl: string | undefined;
  };
  companyInfo: {
    name: string;
    contactUs: string;
    image: string;
    websiteUrl: string;
    privacyPolicyUrl: string;
    slug: string;
  };
  reviews: getReviewsSectionResponse;
  notification: AppNotification | null;
  quickInfo: {
    subCategories: Subcategory[];
    media: Media[];
  };
  properties: {
    geoAvailability: MarketListingBase['geoAvailability'];
    supportedLanguages: string[];
    isAppContentTranslatable: boolean;
    appDependencies: {
      id: string;
      slug: string;
      name: string;
    }[];
    appCollections: {
      slug: string;
      name: string;
    }[];
    hiddenAppCollections: {
      slug: string;
      name: string;
    }[];
  };
  pricingPlans:
    | {
        externalPricingPageUrl?: string;
        isExternalPricing: boolean;
        trialDays: number;
        tax: TaxDetails;
        currencySettings: CurrencySettings;
        companyName: string;
        appName: string;
        plans: Plan[];
        appId: string;
        isFreeApp: boolean;
      }
    | undefined;
  traceableTrader: ITraceableTrader | null;
}

export async function getAppPageDataById({
  id,
  languageCode,
  status,
  siteContext,
  services,
  experiments,
  options,
}: {
  id: string;
  languageCode: string;
  status: SupportedAppStatuses;
  siteContext: ISiteContext;
  services: Services;
  experiments: Experiments;
  options?: AppFetchOptions;
}): Promise<getAppPageDataResponse> {
  const apps = await services.apps.query({ appIds: [id] });
  const app = apps.findByAppId(id);
  if (app.id === '') {
    throw new Error(
      `App with id ${id} not found, languageCode: ${languageCode}, status: ${status}`,
    );
  }

  return getAppPageData({
    app,
    languageCode,
    status,
    siteContext,
    services,
    experiments,
    options,
  });
}

export async function getAppPageDataBySlug({
  slug,
  languageCode,
  status,
  siteContext,
  services,
  experiments,
  options,
}: {
  slug: string;
  languageCode: string;
  status: SupportedAppStatuses;
  siteContext: ISiteContext;
  services: Services;
  experiments: Experiments;
  options?: AppFetchOptions;
}): Promise<getAppPageDataResponse> {
  const apps = await services.apps.query({ slugs: [slug] });
  const app = apps.findBySlug(slug);
  if (!app) {
    throw new PageNotFoundError(
      `App with slug ${slug} not found, languageCode: ${languageCode}, status: ${status}`,
    );
  }

  return getAppPageData({
    app,
    languageCode,
    status,
    siteContext,
    services,
    experiments,
    options,
  });
}

async function getAppPageData({
  app,
  languageCode,
  status,
  siteContext,
  services,
  experiments,
  options = {},
}: {
  app: App;
  languageCode: string;
  status: SupportedAppStatuses;
  siteContext: ISiteContext;
  services: Services;
  experiments: Experiments;
  options?: AppFetchOptions;
}): Promise<getAppPageDataResponse> {
  const { fetchPermissions = false, fetchManagedApps = true } = options;

  const shouldUseAnalyticsService = experiments.enabled(
    'specs.app-market.shouldUseAnalyticsService',
  );

  const isGoogleAnalyticsEnabled = experiments.enabled(
    'specs.app-market.enableGoogleAnalytics',
  );

  const gtag =
    app.id === '53eccee4-c3b2-4123-9d56-9ea914b44c88'
      ? 'G-K3BV3BGJQX'
      : 'G-Q8NFJKPDF2';

  const resellerFlowApp = RESELLER_FLOW_APPS.find(
    ({ appId }) => appId === app.id,
  );

  const [
    { supportedInEditorX, supportedInStudio },
    appsData,
    { marketListing, appDependencies },
    managedApps,
    appPricingPlans,
    reviews,
    { subCategories },
    appCollections,
    hiddenAppCollections,
    marketApp,
    googleAnalyticsConfigs,
    traceableTrader,
    permissions,
    premiumAssets,
    searchResult,
  ] = await Promise.all([
    isAppSupportedInResponsiveEditors({
      services,
      appId: app.id,
      experiments,
    }),
    services.getAppsByAppIds({ appIds: [app.id], status }),
    getAppMarketListingAndDependencies({
      services,
      appId: app.id,
      languageCode,
      status,
      experiments,
    }),
    fetchManagedApps && siteContext.metaSiteId
      ? services.managedApps.get({})
      : undefined,
    status !== 'APPROVED'
      ? getAppPricingPlans({
          services,
          appId: app.id,
          status,
        })
      : undefined,
    getReviewsSectionData({
      services,
      appId: app.id,
    }),
    getSubCategories({
      appId: app.id,
      services,
      languageCode,
    }),
    getAppCollections({
      services,
      appId: app.id,
      languageCode,
    }),
    getHiddenAppCollections({
      services,
      appId: app.id,
      languageCode,
    }),
    services.marketApp.get({ appId: app.id }),
    isGoogleAnalyticsEnabled && shouldUseAnalyticsService
      ? services.googleAnalytics.query({
          id: app.id,
        })
      : undefined,
    services.traceableTrader.get({ id: app.accountId }),
    fetchPermissions
      ? services.getAppPermissions({ appId: app.id, languageCode })
      : undefined,
    resellerFlowApp && siteContext.metaSiteId
      ? services.premiumAssets.query({
          productTypeId: resellerFlowApp.productTypeId,
          metaSiteId: siteContext.metaSiteId,
        })
      : undefined,
    services.searchApps.apps({
      filter: {
        includeAppIds: [app.id],
      },
      customParams: {
        flow: 'installation-flow',
      },
      sortTypes: [SortType.DEFAULT],
      skipRollout: true,
    }),
  ]);

  if (status === 'PUBLISHED' && marketApp.status !== 'PUBLISHED') {
    throw new PageNotFoundError(
      `Attempt to view published app (appId: ${app.id}), but the app is not published`,
    );
  }

  const managedApp = managedApps?.findByAppId(app.id);
  const appData = appsData.findByAppId(app.id);

  const notification = managedApps
    ? await getAppNotification({
        services,
        managedApps,
        app: {
          id: app.id,
          name: marketListing.appName || app.name,
          dependencies: appDependencies,
          isPremiumSiteRequired:
            !!appData.installation.requirements?.premiumSite,
          supportedInEditorX,
          supportedInStudio,
          badges: appData.appBadges,
        },
        siteContext,
        experiments,
        premiumAssets,
        isAppCompatible: searchResult.appIds.length > 0,
      })
    : null;

  const contactUsLink = app.isWixApp
    ? 'https://www.wix.com/contact'
    : `mailto:${marketApp.supportEmails.join(',')}`;

  return {
    app: {
      ...appData,
      ...{
        id: app.id,
        name: marketListing.appName || app.name,
        icon: marketListing.appIcon,
        type: app.isWixApp ? AppType.WIX_APP : AppType.TPA,
        isUpgradable: !!managedApp?.isAppEligibleForUpgrade,
        instanceId: managedApp?.instanceId,
        gTag: isGoogleAnalyticsEnabled
          ? shouldUseAnalyticsService
            ? googleAnalyticsConfigs?.findByAppId(app.id).googleTagId
            : gtag
          : undefined,
        termsOfUseUrl: marketListing.contactInfo?.termsOfUseUrl ?? undefined,
      },
    },
    managedApp,
    overview: {
      benefits: marketListing.benefits,
      demoUrl: marketListing.demoUrl,
      description: marketListing.description,
    },
    companyInfo: {
      name: appData.company.name,
      contactUs: contactUsLink,
      image: appData.company.icon,
      privacyPolicyUrl: appData.company.privacyPolicyUrl,
      websiteUrl: appData.company.website,
      slug: appData.company.slug,
    },
    reviews,
    notification,
    quickInfo: {
      subCategories,
      media: [...marketListing.videoMedia, ...marketListing.imagesMedia],
    },
    properties: {
      geoAvailability: marketListing.geoAvailability,
      supportedLanguages: marketListing.supportedLanguages,
      isAppContentTranslatable: marketListing.isAppContentTranslatable,
      appDependencies: appDependencies.filter(
        (appDependency) => appDependency.isPublished,
      ),
      appCollections,
      hiddenAppCollections,
    },
    pricingPlans: appPricingPlans && {
      externalPricingPageUrl: marketListing.externalPricingPageUrl,
      isExternalPricing: appPricingPlans.isExternalPricing,
      trialDays: appData.freeTrialDays,
      tax: appPricingPlans.taxSettings,
      companyName: appData.company.name,
      appName: appData.name,
      currencySettings: appPricingPlans.currency,
      plans: appPricingPlans.plans.slice(0, 4),
      appId: app.id,
      isFreeApp: appPricingPlans.isFreeApp,
    },
    traceableTrader,
    permissions,
  };
}

async function getAppMarketListingAndDependencies({
  services,
  appId,
  languageCode,
  status,
  experiments,
}: {
  services: Services;
  appId: string;
  languageCode: string;
  status: SupportedAppStatuses;
  experiments: Experiments;
}): Promise<{
  marketListing: MarketListingBase;
  appDependencies: {
    id: string;
    slug: string;
    name: string;
    isPublished: boolean;
  }[];
}> {
  const shouldDoMarketListingPerFieldFallback = experiments.enabled(
    'specs.app-market.MarketListingTranslationWithFallback',
  );

  const marketListings = await services.marketListing.query({
    appIds: [appId],
    languageCodes: [languageCode],
    status,
  });

  // always getting languageCode en app dependencies due to server corrupted data
  const appDependenciesIds = marketListings.findByAppId(
    appId,
    'en',
    shouldDoMarketListingPerFieldFallback,
  ).appDependenciesIds;

  const enrichedAppDependencies =
    appDependenciesIds.length > 0
      ? await enrichAppIds({
          services,
          languageCode,
          appIds: appDependenciesIds,
          experiments,
        })
      : [];

  return {
    marketListing: marketListings.findByAppId(
      appId,
      languageCode,
      shouldDoMarketListingPerFieldFallback,
    ),
    appDependencies: enrichedAppDependencies,
  };
}

async function enrichAppIds({
  services,
  languageCode,
  appIds,
  experiments,
}: {
  services: Services;
  languageCode: string;
  appIds: string[];
  experiments: Experiments;
}): Promise<
  {
    id: string;
    slug: string;
    name: string;
    isPublished: boolean;
  }[]
> {
  const shouldDoMarketListingPerFieldFallback = experiments.enabled(
    'specs.app-market.MarketListingTranslationWithFallback',
  );

  const [marketApps, apps, marketListings] = await Promise.all([
    services.marketApp.query({
      appIds,
    }),
    services.apps.query({
      appIds,
    }),
    services.marketListing.query({
      appIds,
      languageCodes: [languageCode],
      status: 'PUBLISHED',
    }),
  ]);

  return appIds.map((appId) => {
    const marketApp = marketApps.findByAppId(appId);
    const app = apps.findByAppId(appId);
    const marketListing = marketListings.findByAppId(
      appId,
      languageCode,
      shouldDoMarketListingPerFieldFallback,
    );

    return {
      id: appId,
      name: marketListing.appName,
      slug: app.slug,
      isPublished: marketApp.status === 'PUBLISHED',
    };
  });
}

async function getAppCollections({
  services,
  appId,
  languageCode,
}: {
  services: Services;
  appId: string;
  languageCode: string;
}): Promise<
  {
    slug: string;
    name: string;
  }[]
> {
  const tagApps = await services.queryTagApp({
    appIds: [appId],
  });

  const tags = await services.queryTag({
    tagIds: tagApps.tagIds,
    tagType: 'COLLECTION',
    isHidden: false,
    languageCode,
  });

  return tags.map((tag) => {
    return {
      slug: tag.slug,
      name: tag.name,
    };
  });
}

async function getHiddenAppCollections({
  services,
  appId,
  languageCode,
}: {
  services: Services;
  appId: string;
  languageCode: string;
}): Promise<
  {
    slug: string;
    name: string;
  }[]
> {
  const tagApps = await services.queryTagApp({
    appIds: [appId],
  });

  const hiddenTags = await services.queryTag({
    tagIds: tagApps.tagIds,
    tagType: 'COLLECTION',
    isHidden: true,
    languageCode,
  });

  return hiddenTags.map((tag) => {
    return {
      slug: tag.slug,
      name: tag.name,
    };
  });
}
